import { useEffect, useState } from 'react';
import {
  Inbox as InboxIcon, Clock, AlertTriangle, Flame, Hand, RefreshCw, ArrowRight,
  ShieldCheck, BadgeCheck, Gavel, User as UserIcon,
} from 'lucide-react';
import supabase from '../lib/supabase';
import { PageHeader, Section, Badge, Button, EmptyState, Select } from '../components/ui';

type TargetType = 'identity_verification' | 'coach_credential' | 'dispute' | 'refund_request';
type Priority = 'low' | 'normal' | 'high' | 'urgent';

type Row = {
  target_type: TargetType;
  target_id: string;
  opened_at: string;
  subject_user_id: string | null;
  subject_label: string;
  kind_label: string;
  assignee_user_id: string | null;
  priority: Priority | null;
  due_at: string;
  claimed_at: string | null;
  resolved_at: string | null;
  notes: string | null;
  // Joined client-side: human-readable assignee name.
  assignee_name?: string;
};

type Filter = 'all' | 'mine' | 'unclaimed' | 'overdue';

const TYPE_META: Record<TargetType, { icon: any; label: string; href: (id: string) => string }> = {
  identity_verification: { icon: BadgeCheck,  label: 'ID',         href: () => '/verifications' },
  coach_credential:      { icon: ShieldCheck, label: 'Credential', href: (id) => `/coaches?credential=${id}` },
  dispute:               { icon: Gavel,       label: 'Dispute',    href: () => '/disputes' },
  refund_request:        { icon: AlertTriangle, label: 'Refund',   href: () => '/refunds' },
};

const PRIORITY_TONE: Record<Priority, 'red' | 'amber' | 'gray' | 'blue'> = {
  urgent: 'red', high: 'amber', normal: 'gray', low: 'blue',
};

export default function Inbox() {
  const [rows, setRows] = useState<Row[]>([]);
  const [meId, setMeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('all');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    const [{ data }, { data: u }] = await Promise.all([
      (supabase.from as any)('admin_queue_unified')
        .select('*')
        .order('priority', { ascending: false })
        .order('due_at', { ascending: true })
        .limit(500),
      supabase.auth.getUser(),
    ]);
    setMeId(u?.user?.id ?? null);

    // Resolve assignee names in a second batch.
    const items = ((data ?? []) as Row[]);
    const assigneeIds = Array.from(new Set(items.map((r) => r.assignee_user_id).filter((x): x is string => !!x)));
    let nameById: Record<string, string> = {};
    if (assigneeIds.length > 0) {
      const { data: profs } = await supabase
        .from('profiles')
        .select('user_id, full_name')
        .in('user_id', assigneeIds);
      nameById = Object.fromEntries((profs ?? []).map((p) => [p.user_id, p.full_name ?? '(unnamed)']));
    }
    setRows(items.map((r) => ({
      ...r,
      assignee_name: r.assignee_user_id ? (nameById[r.assignee_user_id] ?? '(unnamed)') : undefined,
    })));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const claim = async (r: Row) => {
    setBusy(r.target_id); setMsg(null);
    const { error } = await supabase.rpc('claim_queue_item', {
      p_target_type: r.target_type, p_target_id: r.target_id,
    });
    setBusy(null);
    if (error) { setMsg({ kind: 'error', text: error.message }); return; }
    setMsg({ kind: 'success', text: 'Claimed.' });
    load();
  };

  const release = async (r: Row) => {
    setBusy(r.target_id); setMsg(null);
    const { error } = await supabase.rpc('release_queue_item', {
      p_target_type: r.target_type, p_target_id: r.target_id,
    });
    setBusy(null);
    if (error) { setMsg({ kind: 'error', text: error.message }); return; }
    load();
  };

  const setPriority = async (r: Row, p: Priority) => {
    setBusy(r.target_id); setMsg(null);
    const { error } = await supabase.rpc('set_queue_priority', {
      p_target_type: r.target_type, p_target_id: r.target_id, p_priority: p,
    });
    setBusy(null);
    if (error) { setMsg({ kind: 'error', text: error.message }); return; }
    setRows((prev) => prev.map((x) =>
      x.target_id === r.target_id && x.target_type === r.target_type
        ? { ...x, priority: p } : x));
  };

  const now = Date.now();
  const filtered = rows.filter((r) => {
    if (filter === 'mine')      return r.assignee_user_id === meId;
    if (filter === 'unclaimed') return !r.assignee_user_id;
    if (filter === 'overdue')   return new Date(r.due_at).getTime() < now;
    return true;
  });

  const totals = {
    all:       rows.length,
    mine:      rows.filter((r) => r.assignee_user_id === meId).length,
    unclaimed: rows.filter((r) => !r.assignee_user_id).length,
    overdue:   rows.filter((r) => new Date(r.due_at).getTime() < now).length,
  };

  return (
    <div className="p-8 max-w-6xl">
      <PageHeader
        eyebrow="AceRent admin"
        title="Inbox"
        subtitle="Everything that needs an admin's attention — verifications, coach credentials, disputes."
        actions={<Button variant="secondary" icon={RefreshCw} loading={loading} onClick={load}>Refresh</Button>}
      />

      {msg && (
        <div className={`mb-4 px-4 py-2 rounded-lg text-sm ${msg.kind === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-800'}`}>
          {msg.text}
        </div>
      )}

      <div className="mb-4 flex gap-2 text-xs">
        {(['all', 'mine', 'unclaimed', 'overdue'] as Filter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full border transition ${
              filter === f
                ? 'bg-[#2d5016] text-white border-[#2d5016]'
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
            }`}
          >
            {f === 'all' ? 'All' : f === 'mine' ? 'Mine' : f === 'unclaimed' ? 'Unclaimed' : 'Overdue'}
            <span className={`ml-1.5 ${filter === f ? 'text-white/80' : 'text-gray-500'}`}>
              {totals[f]}
            </span>
          </button>
        ))}
      </div>

      <Section className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading inbox…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={InboxIcon}
            title={filter === 'mine' ? 'Nothing assigned to you'
              : filter === 'unclaimed' ? 'Everything is claimed — nice work'
              : filter === 'overdue' ? 'No overdue items'
              : 'Inbox is empty'}
            description="The inbox unifies identity verifications, coach credentials, and open disputes."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Item</th>
                <th className="text-left px-5 py-3 font-semibold">Opened</th>
                <th className="text-left px-5 py-3 font-semibold">Due</th>
                <th className="text-left px-5 py-3 font-semibold">Priority</th>
                <th className="text-left px-5 py-3 font-semibold">Assignee</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const M = TYPE_META[r.target_type];
                const Icon = M.icon;
                const overdue = new Date(r.due_at).getTime() < now;
                const dueIn = formatDueIn(r.due_at);
                const isMine = r.assignee_user_id === meId;
                return (
                  <tr key={`${r.target_type}:${r.target_id}`} className="border-t border-gray-100 hover:bg-gray-50/60">
                    <td className="px-5 py-3">
                      <div className="flex items-start gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 shrink-0">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs uppercase tracking-wider text-gray-500">{M.label}</div>
                          <div className="font-medium text-gray-900 truncate">{r.subject_label}</div>
                          <div className="text-xs text-gray-500 truncate">{r.kind_label}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-xs text-gray-600">
                      {new Date(r.opened_at).toLocaleString('en-GB', {
                        day: '2-digit', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      {overdue
                        ? <span className="inline-flex items-center gap-1 text-xs text-red-700 font-medium">
                            <AlertTriangle className="w-3.5 h-3.5" /> Overdue · {dueIn}
                          </span>
                        : <span className="inline-flex items-center gap-1 text-xs text-gray-700">
                            <Clock className="w-3.5 h-3.5 text-gray-400" /> {dueIn}
                          </span>}
                    </td>
                    <td className="px-5 py-3">
                      <Select
                        value={r.priority ?? 'normal'}
                        onChange={(e) => setPriority(r, e.target.value as Priority)}
                        className="w-28 h-7 text-xs"
                      >
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </Select>
                      {r.priority === 'urgent' && (
                        <Flame className="w-3.5 h-3.5 text-red-600 inline ml-1" />
                      )}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      {r.assignee_user_id ? (
                        <div className="flex items-center gap-1.5 text-xs">
                          <Badge tone={isMine ? 'lime' : 'gray'} icon={UserIcon}>
                            {isMine ? 'You' : r.assignee_name ?? '(claimed)'}
                          </Badge>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Unclaimed</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <div className="flex justify-end gap-2">
                        {!r.assignee_user_id && (
                          <Button size="sm" variant="secondary" icon={Hand}
                            loading={busy === r.target_id} onClick={() => claim(r)}>Claim</Button>
                        )}
                        {isMine && (
                          <Button size="sm" variant="secondary"
                            loading={busy === r.target_id} onClick={() => release(r)}>Release</Button>
                        )}
                        <a
                          href={M.href(r.target_id)}
                          className="h-8 px-2.5 text-xs rounded-lg border border-gray-200 hover:border-gray-400 inline-flex items-center gap-1 text-gray-700"
                        >
                          Open <ArrowRight className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

function formatDueIn(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(ms);
  const hours = Math.floor(abs / 3_600_000);
  const days = Math.floor(hours / 24);
  if (ms >= 0) {
    if (days >= 1) return `in ${days}d`;
    if (hours >= 1) return `in ${hours}h`;
    const mins = Math.floor(abs / 60_000);
    return `in ${mins}m`;
  }
  if (days >= 1) return `${days}d ago`;
  if (hours >= 1) return `${hours}h ago`;
  const mins = Math.floor(abs / 60_000);
  return `${mins}m ago`;
}
